import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api';

// Async thunks
export const fetchCart = createAsyncThunk(
  'cart/fetchCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getCart();
      return response.data.cart_items;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch cart');
    }
  }
);

export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async (cartItem, { rejectWithValue }) => {
    try {
      const response = await apiService.addToCart(cartItem);
      return response.data.cart_item;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to add item to cart');
    }
  }
);

export const updateCartItem = createAsyncThunk(
  'cart/updateCartItem',
  async ({ id, ...updates }, { rejectWithValue }) => {
    try {
      const response = await apiService.updateCartItem(id, updates);
      return response.data.cart_item;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update cart item');
    }
  }
);

export const removeFromCart = createAsyncThunk(
  'cart/removeFromCart',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.removeFromCart(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to remove item from cart');
    }
  }
);

export const clearCart = createAsyncThunk(
  'cart/clearCart',
  async (_, { rejectWithValue }) => {
    try {
      await apiService.clearCart();
      return true;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to clear cart');
    }
  }
);

export const createCheckoutSession = createAsyncThunk(
  'cart/createCheckoutSession',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.createCheckoutSession();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create checkout session');
    }
  }
);

export const completeCheckout = createAsyncThunk(
  'cart/completeCheckout',
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await apiService.completeCheckout(sessionId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to complete checkout');
    }
  }
);

// Cart slice
const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    items: [],
    loading: false,
    error: null,
    checkoutSession: null,
    checkoutComplete: false,
    newSubscriptions: []
  },
  reducers: {
    resetCheckout: (state) => {
      state.checkoutSession = null;
      state.checkoutComplete = false;
      state.newSubscriptions = [];
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchCart
      .addCase(fetchCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // addToCart
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // updateCartItem
      .addCase(updateCartItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCartItem.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateCartItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // removeFromCart
      .addCase(removeFromCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(item => item.id !== action.payload);
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // clearCart
      .addCase(clearCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearCart.fulfilled, (state) => {
        state.loading = false;
        state.items = [];
      })
      .addCase(clearCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // createCheckoutSession
      .addCase(createCheckoutSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCheckoutSession.fulfilled, (state, action) => {
        state.loading = false;
        state.checkoutSession = action.payload;
      })
      .addCase(createCheckoutSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // completeCheckout
      .addCase(completeCheckout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeCheckout.fulfilled, (state, action) => {
        state.loading = false;
        state.checkoutComplete = true;
        state.newSubscriptions = action.payload.subscriptions;
        state.items = []; // Clear cart after successful checkout
      })
      .addCase(completeCheckout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { resetCheckout } = cartSlice.actions;

// Selectors
export const selectCartItems = state => state.cart.items;
export const selectCartItemCount = state => state.cart.items.length;
export const selectCartTotal = state => {
  return state.cart.items.reduce((total, item) => total + item.amount_per_period, 0);
};
export const selectCheckoutSession = state => state.cart.checkoutSession;
export const selectCheckoutComplete = state => state.cart.checkoutComplete;
export const selectNewSubscriptions = state => state.cart.newSubscriptions;

export default cartSlice.reducer;
